import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, JOINTSIGNING_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import PDF from '@assets/privacy_policy.pdf'
import type {
  Document,
  EditableField,
  EditableFieldValues,
  EasySignGetContextResponse,
} from '@/components/SignDocumentCommon/types'
import { rootStore } from '@/store/Root.store'
import { isLocalDev } from '@/constants/envs'
import { actionReport } from '@/methods/actionReport'
import {
  isIbanField,
  isNumberField,
  isValidIban,
  isValidNumber,
  normalizeIban,
} from '@/methods/validateEditableFields'

const isEditableFieldValid = (field: EditableField, value: string | undefined): boolean => {
  const trimmed = (value ?? '').trim()

  if (!trimmed) return false
  if (isIbanField(field)) return isValidIban(trimmed)
  if (isNumberField(field)) return isValidNumber(trimmed)

  return true
}

export interface PdfFile {
  url: string
  id: string
  name?: string
}

export const useDisplayDocument = (service: string, editableFields: EditableField[] = []) => {
  //  Flags
  const [isLoading, setIsLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)
  const [isDocReady, setIsDocReady] = useState(false)

  const [pdfFile, setPdfFile] = useState<PdfFile>()
  const [fileList, setFileList] = useState<Document[]>([])
  const [currentDocIndex, setCurrentDocIndex] = useState(0)

  const [documentScrolled, setDocumentScrolled] = useState(false)

  const [checkboxKey, setCheckboxKey] = useState(0)
  const [defaultCheckboxChecked, setDefaultCheckboxChecked] = useState(false)

  const [fieldValues, setFieldValues] = useState<EditableFieldValues>({})
  const [resolvedFieldKeys, setResolvedFieldKeys] = useState<string[]>([])
  const lastSubmittedRef = useRef<string | null>(null)

  const setFieldValue = useCallback((key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const registerResolvedFields = useCallback((keys: string[]) => {
    setResolvedFieldKeys((prev) => {
      const merged = new Set(prev)
      const before = merged.size
      keys.forEach((key) => merged.add(key))
      return merged.size === before ? prev : Array.from(merged)
    })
  }, [])

  useEffect(() => {
    setFieldValues({})
    setResolvedFieldKeys([])
    lastSubmittedRef.current = null
  }, [pdfFile?.id])

  useEffect(() => {
    if (!pdfFile?.id) return
    setFieldValues((prev) => {
      let next = prev
      for (const field of editableFields) {
        if (field.documentId !== pdfFile.id || field.value == null || field.key in next) continue
        if (next === prev) next = { ...prev }
        next[field.key] = field.value
      }
      return next
    })
  }, [pdfFile?.id, editableFields])

  const initHookData = async () => {
    setIsLoading(true)
    try {
      const res = await axiosInstance.get<EasySignGetContextResponse>(
        `${API_URL}/${service}/get-context`,
        {
          withCredentials: true,
        }
      )
      devLog(res.data)

      if (res.data?.unsignedDocuments.length === 0) {
        rootStore.ScenarioState.nextPage()
      }

      if (res.data.unsignedDocuments.length === 1) {
        const { data } = await axiosInstance.get(`${API_URL}/${service}/document-url`, {
          withCredentials: true,
          params: {
            documentId: res.data.unsignedDocuments[0].documentId,
          },
        })

        setPdfFile({
          url: data.documentUrl,
          id: res.data.unsignedDocuments[0].documentId,
          name: res.data.unsignedDocuments[0].title,
        })
      } else if (res.data.unsignedDocuments.length > 1) {
        const mappedFileList = res.data.unsignedDocuments.map((item) => ({
          fileId: item.documentId,
          name: item.title,
        }))

        setFileList(mappedFileList)
        await fetchDocumentByIndex(currentDocIndex, mappedFileList)
      }

      setIsDocReady(true)
    } catch (e) {
      devLog(e)
    }
    setIsLoading(false)
  }

  const fetchDocumentByIndex = async (index: number, documents?: Document[]): Promise<void> => {
    const docsToUse = documents || fileList
    if (!docsToUse || index < 0 || index >= docsToUse.length) {
      devLog('Invalid document index for fetching fileList: ', docsToUse, ' index: ', index)
      return
    }

    const doc = docsToUse[index]

    try {
      const { data } = await axiosInstance.get<{ documentUrl: string }>(
        `/${service}/document-url`,
        {
          withCredentials: true,
          params: {
            documentId: doc.fileId,
          },
        }
      )

      setPdfFile({
        id: doc.fileId,
        name: doc.name,
        url: data.documentUrl,
      })
    } catch (error) {
      devLog('Error fetching document URL', error)
    }
  }

  const fetchNextDocument = async () => {
    if (!fileList || !fileList[currentDocIndex]) return
    setIsDocReady(false)

    try {
      if (currentDocIndex < fileList.length - 1) {
        setCurrentDocIndex(currentDocIndex + 1)
        setDefaultCheckboxChecked(false)
        setCheckboxKey((prev) => prev + 1)

        setIsDocReady(false)

        await fetchDocumentByIndex(currentDocIndex + 1)

        setDocumentScrolled(false)

        setIsDocReady(true)
      }
    } catch (error) {
      devLog(error)
    }
  }

  const submitFieldValues = useCallback(
    async (documentId?: string): Promise<boolean> => {
      const fields = editableFields
        .filter((field) => !documentId || field.documentId === documentId)
        .map((field) => {
          const trimmed = (fieldValues[field.key] ?? '').trim()
          return {
            documentId: field.documentId,
            key: field.key,
            value: isIbanField(field) ? normalizeIban(trimmed) : trimmed,
          }
        })
        .filter((field) => field.value)

      if (!fields.length) return true

      const serialized = JSON.stringify(fields)
      if (serialized === lastSubmittedRef.current) return true
      lastSubmittedRef.current = serialized

      try {
        await axiosInstance.post(
          `${API_URL}/${JOINTSIGNING_SERVICE_PATH}/set-user-editable-fields`,
          { fields },
          { withCredentials: true }
        )
        return true
      } catch (e) {
        lastSubmittedRef.current = null
        devLog(e)
        return false
      }
    },
    [editableFields, fieldValues]
  )

  const handleSign = async (): Promise<boolean> => {
    const docId = pdfFile?.id || fileList[currentDocIndex]?.fileId

    if (!docId) {
      devLog('No document ID found for signing')
      return false
    }

    try {
      setBtnLoading(true)

      const submitted = await submitFieldValues(docId)
      if (!submitted) {
        setBtnLoading(false)
        return false
      }

      if (!isLocalDev) {
        if (fileList.length && currentDocIndex < fileList.length - 1) {
          fetchNextDocument()
        } else {
          actionReport({
            type: 'event.onboarding-web.agreement.ALL_AGREEMENTS_CONFIRMED',
            payload: {},
          })

          rootStore.ScenarioState.nextPage()
        }
      }

      setBtnLoading(false)
      return true
    } catch (e) {
      setBtnLoading(false)
      devLog(e)
      return false
    }
  }

  useEffect(() => {
    initHookData()
  }, [])

  const currentDocumentFields = useMemo(
    () => editableFields.filter((field) => field.documentId === pdfFile?.id),
    [editableFields, pdfFile?.id]
  )

  const allFieldsValid = useMemo(
    () =>
      currentDocumentFields.every(
        (field) =>
          resolvedFieldKeys.includes(field.key) &&
          isEditableFieldValid(field, fieldValues[field.key])
      ),
    [currentDocumentFields, fieldValues, resolvedFieldKeys]
  )

  const isButtonDisabled = useMemo(
    () => !defaultCheckboxChecked || !allFieldsValid,
    [defaultCheckboxChecked, allFieldsValid]
  )

  if (isLocalDev) {
    return {
      isLoading: false,
      btnLoading: false,
      isDocReady: true,
      pdfFile: {
        url: PDF,
        id: editableFields[0]?.documentId ?? 'docID1',
        name: 'Doc name',
      },
      documentScrolled: false,
      setDocumentScrolled,
      checkboxKey,
      defaultCheckboxChecked,
      setDefaultCheckboxChecked,
      isButtonDisabled,
      editableFields,
      fieldValues,
      setFieldValue,
      registerResolvedFields,
      handleSign: async () => {
        rootStore.ScenarioState.nextPage()
        return true
      },
    }
  }

  return {
    isLoading,
    btnLoading,
    isDocReady,
    pdfFile,
    fileList,
    currentDocIndex,
    documentScrolled,
    setDocumentScrolled,
    checkboxKey,
    defaultCheckboxChecked,
    setDefaultCheckboxChecked,
    handleSign,
    isButtonDisabled,
    editableFields,
    fieldValues,
    setFieldValue,
    registerResolvedFields,
  }
}
